import { useState, useMemo } from 'react';
import { ChevronLeft, Search, Paperclip, Calendar, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../../lib/api';
import { toast } from 'react-hot-toast';

// Helper function to get initials from name
const getInitials = (firstName, lastName) => {
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  return (first + last).toUpperCase() || '?';
};

// Helper function to get status badge styling
const getStatusBadge = (status) => {
  const statusMap = {
    pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
    accepted: { label: 'Accepted', className: 'bg-emerald-100 text-emerald-700' },
    rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
  };
  return statusMap[status] || { label: status || 'Unknown', className: 'bg-gray-100 text-gray-700' };
};

export default function CreateRequests() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDropdown, setShowQuoteDropdown] = useState(false);
  const [quoteSearchTerm, setQuoteSearchTerm] = useState('');

  // form = body exactly:
  // {
  //   price,
  //   estimated_days,
  //   notes,
  //   terms_and_conditions
  // }
  const [price, setPrice] = useState('');
  const [estimatedDays, setEstimatedDays] = useState('');
  const [notes, setNotes] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [errors, setErrors] = useState({});

  // Fetch quote requests from API
  const {
    data: quoteRequests,
    isLoading: isLoadingQuotes,
    error: quotesError,
    refetch: refetchQuotes,
  } = useQuery({
    queryKey: ['quoteRequests'],
    queryFn: async () => {
      const response = await api.get('/company/quotes/requests');
      return response.data.data || [];
    },
  });

  // Filter quotes based on search term
  const filteredQuotes = useMemo(() => {
    if (!quoteRequests) return [];
    const search = quoteSearchTerm.toLowerCase();
    return quoteRequests.filter((quote) => {
      const fullName = `${quote.first_name || ''} ${quote.last_name || ''}`.toLowerCase();
      const serviceName = quote.service?.name?.toLowerCase() || '';
      const boatInfo = `${quote.boat_type || ''} ${quote.boat_make || ''} ${quote.boat_location || ''}`.toLowerCase();

      return (
        fullName.includes(search) ||
        (quote.email || '').toLowerCase().includes(search) ||
        (quote.phone || '').includes(search) ||
        serviceName.includes(search) ||
        boatInfo.includes(search)
      );
    });
  }, [quoteRequests, quoteSearchTerm]);

  // Create offer mutation
  const createOfferMutation = useMutation({
    mutationFn: async () => {
      if (!selectedQuote) throw new Error('No quote selected');

      const payload = {
        price: Number(price),
        estimated_days: Number(estimatedDays),
        notes: notes || null,
        terms_and_conditions: termsAndConditions || null,
      };

      const response = await api.post(`/company/quotes/${selectedQuote.id}/offers`, payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Offer created successfully!');
      queryClient.invalidateQueries(['quoteRequests']);
      queryClient.invalidateQueries(['companyQuotes']);

      // Reset form
      setSelectedQuote(null);
      setPrice('');
      setEstimatedDays('');
      setNotes('');
      setTermsAndConditions('');
      setErrors({});

      navigate(-1);
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Failed to create offer';
      toast.error(message);
    },
  });

  const today = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const handleQuoteSelect = (quote) => {
    setSelectedQuote(quote);
    setShowQuoteDropdown(false);
    setQuoteSearchTerm('');
    if (errors.quote) setErrors((prev) => ({ ...prev, quote: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!selectedQuote) {
      newErrors.quote = 'Please select a quote request';
    }
    if (!price || Number(price) <= 0) {
      newErrors.price = 'Please enter a valid price';
    }
    if (!estimatedDays || Number(estimatedDays) <= 0) {
      newErrors.estimated_days = 'Please enter valid estimated days';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      createOfferMutation.mutate();
    }
  };

  return (
    <section className="min-h-screen text-sm">
      {/* Fixed Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-sm">
                  <Paperclip className="w-5 h-5 text-white rotate-45" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Create Offer</h1>
                  <p className="text-xs text-gray-500">Create offer for quote request</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
              <Calendar className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-xs font-medium text-blue-900">{today}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <form onSubmit={handleSubmit} className="px-4 py-5 space-y-5">
        {/* Quote Request Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Quote Request</h2>

          <div className="space-y-4">
            {/* Quote Dropdown */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Select Quote Request *
              </label>
              <button
                type="button"
                onClick={() => setShowQuoteDropdown(!showQuoteDropdown)}
                className={`w-full px-3 py-2.5 border rounded-lg text-left flex items-center justify-between hover:border-orange-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 text-sm bg-white transition ${
                  errors.quote ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                {selectedQuote ? (
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 text-xs">
                      {selectedQuote.first_name} {selectedQuote.last_name} •{' '}
                      {selectedQuote.service?.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedQuote.boat_type} • {selectedQuote.boat_make} ({selectedQuote.boat_year})
                    </p>
                    <p className="text-xs text-gray-500">{selectedQuote.boat_location}</p>
                  </div>
                ) : (
                  <span className="text-gray-500">Choose a quote request...</span>
                )}
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
              </button>
              {errors.quote && (
                <p className="text-xs text-red-600 mt-1">{errors.quote}</p>
              )}

              {showQuoteDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowQuoteDropdown(false)}
                  />
                  <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-lg shadow-xl border border-gray-200 z-50 text-xs overflow-hidden">
                    <div className="p-3 bg-gradient-to-r from-orange-500 to-orange-600">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                        <input
                          type="text"
                          placeholder="Search quote requests..."
                          value={quoteSearchTerm}
                          onChange={(e) => setQuoteSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 bg-white/20 rounded-lg text-white placeholder-white/70 focus:outline-none focus:bg-white/30 text-sm"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {isLoadingQuotes ? (
                        <div className="px-4 py-8 text-center text-gray-500 text-xs">
                          <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-orange-500" />
                          <p>Loading requests...</p>
                        </div>
                      ) : quotesError ? (
                        <div className="px-4 py-8 text-center text-gray-500 text-xs">
                          <p className="text-red-600 mb-2">Failed to load requests</p>
                          <button
                            type="button"
                            onClick={() => refetchQuotes()}
                            className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-xs hover:bg-orange-700 transition"
                          >
                            Retry
                          </button>
                        </div>
                      ) : filteredQuotes.length > 0 ? (
                        filteredQuotes.map((quote) => {
                          const initials = getInitials(quote.first_name, quote.last_name);
                          const statusBadge = getStatusBadge(quote.status);
                          return (
                            <button
                              type="button"
                              key={quote.id}
                              onClick={() => handleQuoteSelect(quote)}
                              className="w-full text-left px-4 py-3 hover:bg-orange-50 flex items-start gap-2.5 border-b border-gray-100 last:border-0 transition"
                            >
                              <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                {initials}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <p className="font-medium text-gray-900 text-sm">
                                    {quote.first_name} {quote.last_name}
                                  </p>
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}
                                  >
                                    {statusBadge.label}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mb-0.5">
                                  {quote.service?.name} • {quote.boat_type} • {quote.boat_make}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {quote.boat_location} • {quote.email}
                                </p>
                              </div>
                            </button>
                          );
                        })
                      ) : (
                        <div className="px-4 py-8 text-center text-gray-500 text-xs">
                          <Search className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                          <p>No quote requests found</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Offer Fields = API Body */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <Paperclip className="w-4 h-4 text-orange-600 rotate-45" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Offer Details</h2>
              <p className="text-xs text-gray-600">
                These fields map exactly to the API body
              </p>
            </div>
          </div>

          {/* price */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Price *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => {
                setPrice(e.target.value);
                if (errors.price) setErrors((prev) => ({ ...prev, price: '' }));
              }}
              placeholder="1500.00"
              className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm ${
                errors.price ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.price && (
              <p className="text-xs text-red-600 mt-1">{errors.price}</p>
            )}
          </div>

          {/* estimated_days */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Estimated Days *
            </label>
            <input
              type="number"
              min="1"
              value={estimatedDays}
              onChange={(e) => {
                setEstimatedDays(e.target.value);
                if (errors.estimated_days) setErrors((prev) => ({ ...prev, estimated_days: '' }));
              }}
              placeholder="3"
              className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm ${
                errors.estimated_days ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.estimated_days && (
              <p className="text-xs text-red-600 mt-1">{errors.estimated_days}</p>
            )}
          </div>

          {/* notes */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Notes
            </label>
            <textarea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="We can complete the survey within 3 business days"
              className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
            />
          </div>

          {/* terms_and_conditions */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Terms & Conditions
            </label>
            <textarea
              rows={4}
              value={termsAndConditions}
              onChange={(e) => setTermsAndConditions(e.target.value)}
              placeholder="Payment due upon completion"
              className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-gray-400/20 focus:border-gray-400 text-sm"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createOfferMutation.isPending}
            className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {createOfferMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating Offer...
              </>
            ) : (
              'Create Offer'
            )}
          </button>
        </div>
      </form>
    </section>
  );
}
